import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import {
  PopPaper,
  PopupContext,
  Portal,
  TVisibilityTrigger,
  useCloseByClickHandler,
} from 'core-components/library/popup'

import { BracesIcon, CopyIcon, LinkIcon } from './icons'
import type { TJsonCopyMode } from './lib'
import { NETWORK_DEBUG_TRANSLATION_KEYS } from './translations'

import styles from './JsonNodeMenu.module.scss'

const KEYS = NETWORK_DEBUG_TRANSLATION_KEYS

const ITEMS = [
  { mode: 'value', text: KEYS.copyValue, Icon: CopyIcon },
  { mode: 'path', text: KEYS.copyPath, Icon: LinkIcon },
  { mode: 'keyValue', text: KEYS.copyKeyValue, Icon: BracesIcon },
] as const

/** Escape closes the topmost popup, so the menu goes before the dialog under it. */
const CloseOnEscape = ({ onClose }: { onClose: () => void }) => {
  const { register, unregister } = useContext(PopupContext)

  useEffect(() => {
    register(onClose)

    return () => unregister(onClose)
  }, [register, unregister, onClose])

  return null
}

interface IJsonNodeMenuProps {
  x: number
  y: number
  /** List items have an index, not a key, so they cannot be copied as `"key": value`. */
  hasKey: boolean
  onCopy: (mode: TJsonCopyMode) => void
  onClose: () => void
}

/** The copy menu opened with a right click on a tree row, at the pointer. */
export const JsonNodeMenu = ({ x, y, hasKey, onCopy, onClose }: IJsonNodeMenuProps) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ left: x, top: y })

  const setVisible = useCallback((visible: boolean) => !visible && onClose(), [onClose])

  const { popupAttributeData } = useCloseByClickHandler({
    visible: true,
    rootRef,
    callVisibleSetter: setVisible,
    visibilityTrigger: TVisibilityTrigger.Click,
    useOwnContext: true,
  })

  useLayoutEffect(() => {
    const node = rootRef.current

    if (!node) {
      return
    }

    const { width, height } = node.getBoundingClientRect()

    setPosition({
      left: Math.max(0, Math.min(x, window.innerWidth - width)),
      top: y + height > window.innerHeight ? Math.max(0, y - height) : y,
    })
  }, [x, y])

  useEffect(() => {
    window.addEventListener('resize', onClose)
    document.addEventListener('scroll', onClose, true)

    return () => {
      window.removeEventListener('resize', onClose)
      document.removeEventListener('scroll', onClose, true)
    }
  }, [onClose])

  return (
    <Portal>
      <CloseOnEscape onClose={onClose} />
      <div
        ref={rootRef}
        className={styles.root}
        style={position}
        role="menu"
        onContextMenu={(event) => event.preventDefault()}
        {...popupAttributeData}
      >
        <PopPaper className={styles.paper}>
          {ITEMS.filter(({ mode }) => hasKey || mode !== 'keyValue').map(({ mode, text, Icon }) => (
            <button
              key={mode}
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={() => onCopy(mode)}
            >
              <Icon className={styles.icon} />
              <Translation text={text} span />
            </button>
          ))}
        </PopPaper>
      </div>
    </Portal>
  )
}

JsonNodeMenu.displayName = 'JsonNodeMenu'
